import React from 'react';

import {
  Alert,
  Box,
  Card,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import Spinner from '~/components/Common/Spinner';

import { AVAILABLE_REGIONS_LIB_REGIONS } from '~/consts/agentHub';
import type { CommissionsConditionTypes, RuleCondition } from '~/consts/agentHub';

import useCurrentBrand from '~/hooks/useCurrentBrand';

import type { Dayjs } from '~/services/TimeService';
import { SYDNEY_TIMEZONE, formatDateSlashes } from '~/services/TimeService';

import type { Commission } from '~/types/services/agentHub';

import { buildCommissionConditionBreakdown } from '~/utils/agentHub';
import { capitalise } from '~/utils/stringUtils';

import type {
  CommissionConditionOperator,
  CommissionFormState,
  CommissionGroupOperator,
  CommissionTypeChoice,
} from './CommissionDialog.types';
import { CommissionGroupEditor } from './CommissionGroupEditor';

export const SHRUNK_LABEL = {
  textField: {
    InputLabelProps: { shrink: true },
  },
};

export const INPUT_BORDER = {
  sx: {
    borderRadius: 0.5,
  },
};

export type CommissionFormExternalFields = {
  startDate?: boolean;
  endDate?: boolean;
  commissionType?: boolean;
  description?: boolean;
};

export type CommissionFormLockedFields = CommissionFormExternalFields;
export type CommissionFormHiddenFields = CommissionFormExternalFields;

interface Props {
  value: CommissionFormState;
  onChange: (next: CommissionFormState) => void;
  data?: Commission;
  lockedFields?: CommissionFormLockedFields;
  hiddenFields?: CommissionFormHiddenFields;
  isLoadingConditions?: boolean;
  creator?: { user: { href: string; label: string } };
  updater?: { user: { href: string; label: string } };
  isLoadingCommissionEditHistory?: boolean;
  addCondition: (operator: CommissionGroupOperator, conditionId: string, newCondition: RuleCondition) => void;
  handleChangeCondition: (
    groupId: CommissionGroupOperator,
    conditionId: string,
    type: CommissionsConditionTypes,
  ) => void;
  handleConditionOperatorChange: (
    groupOperator: CommissionGroupOperator,
    conditionId: string,
    operator: CommissionConditionOperator,
  ) => void;
  handleClearCondition: (operator: CommissionGroupOperator, conditionId: string) => void;
  handleAppendCondition: (operator: CommissionGroupOperator) => void;
}

function CommissionForm({
  value,
  onChange,
  data,
  lockedFields,
  hiddenFields,
  isLoadingConditions = false,
  creator,
  updater,
  isLoadingCommissionEditHistory = false,
  addCondition,
  handleChangeCondition,
  handleConditionOperatorChange,
  handleClearCondition,
  handleAppendCondition,
}: Props) {
  const brand = useCurrentBrand();
  const commissionConditionBreakdown = buildCommissionConditionBreakdown(Object.values(value.commissionGroups));

  const patch = (partial: Partial<CommissionFormState>) => {
    onChange({ ...value, ...partial });
  };

  return (
    <Stack direction="column" spacing={2}>
      {data?.hasReachedLimit && (
        <Alert severity="warning">
          This commission rule has reached its limit of usage and is no longer active. If you change it, it can
          accidentally become active again.
        </Alert>
      )}
      {!hiddenFields?.description && (
        <Stack direction="row" gap={2}>
          <TextField
            placeholder="e.g. Flight Center UK - Hotel + Tours"
            name="description"
            value={value.description}
            onChange={(event) => patch({ description: event.target.value })}
            label="Description"
            required
            fullWidth
            disabled={lockedFields?.description}
            InputLabelProps={SHRUNK_LABEL.textField.InputLabelProps}
            InputProps={INPUT_BORDER}
          />
        </Stack>
      )}
      <Stack direction="row" gap={2} justifyContent="space-between">
        {!hiddenFields?.commissionType && (
          <FormControl fullWidth>
            <InputLabel
              sx={{ backgroundColor: 'background.paper', padding: '0 4px' }}
              shrink
              id="agent-hub-commissions-discount-type-input-label"
            >
              Commission type
            </InputLabel>
            <Select
              value={value.commissionType}
              displayEmpty
              onChange={(event) => patch({ commissionType: event.target.value as CommissionTypeChoice })}
              renderValue={(selected) => {
                if (!selected) {
                  return <Typography color="text.secondary">Select commission type</Typography>;
                }
                return capitalise(selected);
              }}
              label="Commission type"
              required
              fullWidth
              disabled={lockedFields?.commissionType}
              sx={INPUT_BORDER.sx}
            >
              <MenuItem value="base">Base</MenuItem>
              <MenuItem value="additional">Additional</MenuItem>
              <MenuItem value="incentive">Incentive</MenuItem>
            </Select>
          </FormControl>
        )}
        <TextField
          name="discountAmount"
          label="Commission percentage"
          value={value.commissionPercentage}
          onChange={(event) => patch({ commissionPercentage: event.target.value })}
          type="number"
          required
          fullWidth
          placeholder="e.g. 8%"
          slotProps={{
            htmlInput: { min: 1, max: 100 },
            input: INPUT_BORDER,
            inputLabel: SHRUNK_LABEL.textField.InputLabelProps,
          }}
        />
      </Stack>

      <Stack direction="row" justifyContent="flex-end" gap={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={value.includesTaxesAndFees}
              onChange={(event) => patch({ includesTaxesAndFees: event.target.checked })}
              size="small"
            />
          }
          label="Include taxes and fees"
        />
        <Tooltip title="If disabled, the LE margin/POS commission for the offer will not be counted as part of the commission, so if the total is $1000 but LE margin is $100, the commission will be applied in the remaining $900">
          <FormControlLabel
            control={
              <Checkbox
                checked={value.includesMargin}
                onChange={(event) => patch({ includesMargin: event.target.checked })}
                size="small"
              />
            }
            label="Include margin"
          />
        </Tooltip>
      </Stack>

      {value.commissionType === 'base' && (
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="button" color="secondary">
              Base commission settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              These options only apply when the commission type is base.
            </Typography>
          </Box>
          <Stack spacing={1}>
            <TextField
              value={value.priorityInput}
              onChange={(event) => patch({ priorityInput: event.target.value })}
              InputProps={{ inputProps: { min: 0, step: 1 }, ...INPUT_BORDER }}
              label="Priority"
              type="number"
              fullWidth
              placeholder="e.g. 0 (highest)"
              InputLabelProps={SHRUNK_LABEL.textField.InputLabelProps}
            />
            <Stack direction="row" justifyContent="flex-end">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value.canHaveAdditionalCommissions}
                    onChange={(event) => patch({ canHaveAdditionalCommissions: event.target.checked })}
                    size="small"
                  />
                }
                label="Can apply additional commissions"
              />
            </Stack>
          </Stack>
        </Stack>
      )}

      {(!hiddenFields?.startDate || !hiddenFields?.endDate) && (
        <Stack direction="row" gap={2}>
          {!hiddenFields?.startDate && (
            <Stack flex={1}>
              <DatePicker
                value={value.startDate}
                onChange={(date: Dayjs | null) => patch({ startDate: date })}
                format="DD/MM/YYYY"
                name="startDate"
                label="Start date"
                disabled={lockedFields?.startDate}
                timezone={SYDNEY_TIMEZONE}
                sx={INPUT_BORDER.sx}
                slotProps={{ textField: { ...SHRUNK_LABEL.textField, InputProps: INPUT_BORDER } }}
              />
            </Stack>
          )}
          {!hiddenFields?.endDate && (
            <Stack flex={1}>
              <DatePicker
                value={value.endDate}
                onChange={(date: Dayjs | null) => patch({ endDate: date })}
                format="DD/MM/YYYY"
                disablePast
                name="endDate"
                label="End date"
                disabled={lockedFields?.endDate}
                timezone={SYDNEY_TIMEZONE}
                slotProps={{ textField: { ...SHRUNK_LABEL.textField, InputProps: INPUT_BORDER } }}
              />
            </Stack>
          )}
        </Stack>
      )}
      <Stack direction="row" gap={2}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel
            sx={{ backgroundColor: 'background.paper', padding: '0 4px' }}
            shrink
            id="agent-hub-commissions-regions-input-label"
          >
            Regions
          </InputLabel>
          <Select
            displayEmpty
            name="regions"
            labelId="agent-hub-commissions-regions-input-label"
            multiple
            value={value.regions}
            input={<OutlinedInput label="Regions" />}
            sx={INPUT_BORDER.sx}
            renderValue={(selected: Array<string>) => {
              if (selected.length === 0) {
                return <Typography color="text.secondary">All regions</Typography>;
              }
              return selected.join(', ');
            }}
            onChange={(event) => {
              patch({ regions: event.target.value as Array<string> });
            }}
          >
            {AVAILABLE_REGIONS_LIB_REGIONS.map((region) => (
              <MenuItem key={region.code} value={region.code}>
                <Checkbox checked={value.regions.includes(region.code)} />
                <ListItemText primary={region.name} />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>If no region is selected then the commission rule applies to all regions.</FormHelperText>
        </FormControl>
        <Stack flex={1} spacing={1}>
          <TextField
            name="limit"
            InputProps={{ inputProps: { min: 0 }, ...INPUT_BORDER }}
            label="Limit (of usage)"
            value={value.limit}
            onChange={(event) => patch({ limit: event.target.value })}
            type="number"
            fullWidth
            disabled={!value.shouldIncludeLimit}
            InputLabelProps={SHRUNK_LABEL.textField.InputLabelProps}
            placeholder="e.g. 5"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={value.shouldIncludeLimit}
                onChange={(event) => patch({ shouldIncludeLimit: event.target.checked })}
                size="small"
              />
            }
            label="Set limit to commission"
          />
        </Stack>
      </Stack>
      {value.commissionType === 'incentive' && (
        <Stack direction="row" gap={2} flexWrap="wrap">
          {(value.regions.length > 0 ? value.regions : AVAILABLE_REGIONS_LIB_REGIONS.map((region) => region.code)).map(
            (region) => (
              <TextField
                key={region}
                name={`commissionLimit-${region}`}
                label={`Max amount (${region})`}
                value={value.commissionValueLimits[region] ?? ''}
                onChange={(event) =>
                  patch({
                    commissionValueLimits: {
                      ...value.commissionValueLimits,
                      [region]: event.target.value,
                    },
                  })
                }
                type="number"
                sx={{ flex: '1 1 180px', minWidth: 160 }}
                placeholder="e.g. 100"
                slotProps={{
                  htmlInput: { min: 1 },
                  input: INPUT_BORDER,
                  inputLabel: SHRUNK_LABEL.textField.InputLabelProps,
                }}
              />
            ),
          )}
        </Stack>
      )}
      <Divider />

      <Stack spacing={2}>
        <Box>
          <Typography variant="button" color="secondary">
            Conditions:
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Conditions are organized into groups. Choose AND or OR for each group, then add as many conditions as needed
            inside it. If there are both AND and OR conditions, both must be satisfied for the commission rule to be
            met.
          </Typography>
        </Box>

        {isLoadingConditions && <Spinner size={14} />}
        <Stack direction="column" spacing={3}>
          {Object.values(value.commissionGroups).map((group) => (
            <CommissionGroupEditor
              key={group.id}
              group={group}
              brand={brand}
              addCondition={addCondition}
              handleAppendCondition={handleAppendCondition}
              handleChangeCondition={handleChangeCondition}
              handleConditionOperatorChange={handleConditionOperatorChange}
              handleClearCondition={handleClearCondition}
            />
          ))}
        </Stack>
      </Stack>

      {isLoadingCommissionEditHistory && <Spinner size={14} />}

      {creator && (
        <Stack>
          <Typography variant="caption" color="secondary">
            Created By: <Link href={`/users/${creator.user.href}`}>{creator.user.label}</Link> at{' '}
            {formatDateSlashes(data?.createdAt)}
          </Typography>
          {updater && (
            <Typography variant="caption" color="secondary">
              Edited By: <Link href={`/users/${updater.user.href}`}>{updater.user.label}</Link> at{' '}
              {formatDateSlashes(data?.updatedAt)}
            </Typography>
          )}
        </Stack>
      )}

      {commissionConditionBreakdown.length > 0 && (
        <Card variant="outlined" sx={{ borderRadius: 1, p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="button" color="secondary">
              Commission condition breakdown
            </Typography>
            <Stack spacing={0.5}>
              {commissionConditionBreakdown.map((line, index) => (
                <Typography key={`${line}-${index}`} variant="body2">
                  {line}
                  {index < commissionConditionBreakdown.length - 1 && (
                    <Typography component="span" variant="body2" fontWeight={600}>
                      {' '}
                      AND
                    </Typography>
                  )}
                </Typography>
              ))}
            </Stack>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}

export default CommissionForm;
